/**
 * O3 (Salon Owner localization & polish) — owner-portal strings for the
 * "myBusiness" area. Merged into the runtime dictionary by i18n/owner/index.ts and read
 * through LanguageContext's t(). Every key needs BOTH an en and an ar value.
 *
 * Scope: My Business (Business Details, Branches, Categories, Services), the
 * standalone /services page, Packages, and the branch / service / hours
 * components those screens use. Keys are prefixed `mb.`; a few existing inline
 * keys whose Arabic was missing or wrong are overridden at the bottom of each
 * language block (marked "override").
 */
import type { OwnerStrings } from './types';

export const myBusinessStrings: OwnerStrings = {
    en: {
        /* ── Common ─────────────────────────────────────────────── */
        'mb.common.tryAgain': 'Try again',
        'mb.common.retry': 'Retry',
        'mb.common.loading': 'Loading…',
        'mb.common.saving': 'Saving…',
        'mb.common.edit': 'Edit',
        'mb.common.delete': 'Delete',
        'mb.common.remove': 'Remove',
        'mb.common.cancel': 'Cancel',
        'mb.common.save': 'Save',
        'mb.common.add': 'Add',
        'mb.common.close': 'Close',
        'mb.common.dismiss': 'Dismiss',
        'mb.common.more': 'More',
        'mb.common.all': 'All',
        'mb.common.active': 'Active',
        'mb.common.inactive': 'Inactive',
        'mb.common.activeInactive': '{active} active · {inactive} inactive',
        'mb.common.discardChanges': 'Discard unsaved changes?',
        'mb.common.optional': '(optional)',
        'mb.common.upload': 'Upload',
        'mb.common.replace': 'Replace',
        'mb.common.uploading': 'Uploading…',

        /* ── Durations (src/lib/humanDuration.ts) ───────────────── */
        'mb.duration.minutes.one': '{n} min',
        'mb.duration.minutes.two': '{n} min',
        'mb.duration.minutes.few': '{n} min',
        'mb.duration.minutes.other': '{n} min',
        'mb.duration.hours.one': '{n} h',
        'mb.duration.hours.two': '{n} h',
        'mb.duration.hours.few': '{n} h',
        'mb.duration.hours.other': '{n} h',
        'mb.duration.join': '{h} {m}',
        'mb.duration.range': '{min} – {max}',

        /* ── Delete confirm (DeleteModal) ───────────────────────── */
        'mb.deleteModal.title': 'Delete {name}?',
        'mb.deleteModal.body': 'This permanently removes it and can’t be undone.',
        'mb.deleteModal.confirm': 'Delete',

        /* ── Branches ───────────────────────────────────────────── */
        'mb.branches.title': 'Branches',
        'mb.branches.subtitle': 'Each location keeps its own address, hours, team and services. Customers pick the branch that suits them when they book.',
        'mb.branches.intro.strong': 'One branch is enough to get going.',
        'mb.branches.intro.body': 'Add more anytime — each booking goes to the branch the customer picks.',
        'mb.branches.loadFailed': 'Couldn’t load your branches.',
        'mb.branches.empty.title': 'You haven’t added a branch yet',
        'mb.branches.empty.desc': 'Add the address where you take bookings. If you run more than one salon, add each one — customers can pick the closest when they book.',
        'mb.branches.empty.cta': '+ Add your first branch',
        'mb.branches.empty.hint': 'Don’t have your address handy?',
        'mb.branches.empty.hintDetails': 'Continue with Business Details',
        'mb.branches.empty.hintOr': 'or',
        'mb.branches.empty.hintServices': 'browse Services',
        'mb.branches.count': '{n} of {cap} branches',
        'mb.branches.add': '+ Add branch',
        'mb.branches.hq': 'HQ',
        'mb.branches.editAria': 'Edit {name}',
        'mb.branches.team': 'Branch team',
        'mb.branches.teamAria': 'Branch team for {name}',
        'mb.branches.breakRulesAria': 'Break rules for {name}',
        'mb.branches.moreAria': 'More actions for {name}',
        'mb.branches.delete': 'Delete branch',
        'mb.branches.toast.created': 'Branch added.',
        'mb.branches.toast.updated': 'Branch updated.',
        'mb.branches.toast.deleted': 'Branch deleted.',
        'mb.branches.toast.createFailed': 'Couldn’t add the branch. Please try again.',
        'mb.branches.toast.updateFailed': 'Couldn’t update the branch. Please try again.',
        'mb.branches.toast.deleteFailed': 'Couldn’t delete the branch. Please try again.',
        'mb.branches.toast.teamLoadFailed': 'Couldn’t load your team. Please try again.',
        'mb.branches.toast.teamUpdated': 'Team updated for {name}.',
        /* ── Branch team (BranchRosterModal) ── */
        'mb.common.saveChanges': 'Save changes',
        'mb.team.title': 'Branch team — {name}',
        'mb.team.subtitle': 'Choose who works at this branch. People tagged “All branches” work at every branch; unticking them here keeps them at your other branches only.',
        'mb.team.count': '{n} of {total} active team members',
        'mb.team.added': '+{n} added',
        'mb.team.removed': '−{n} removed',
        'mb.team.empty': 'You don’t have any active team members yet. Add them in Manage Staff, then come back to choose who works at this branch.',
        'mb.team.searchPlaceholder': 'Search {n} team members…',
        'mb.team.searchAria': 'Search team members',
        'mb.team.allBranchesTag': 'All branches',
        'mb.team.tipLabel': 'Tip:',
        'mb.team.tip': 'you can also choose each person’s branches in Manage Staff. This view is quickest when you open a new branch.',
        'mb.team.noChanges': 'No changes',
        'mb.team.addFailed': 'Couldn’t add to this branch. Please try again.',
        'mb.team.removeFailed': 'Couldn’t remove from this branch. Please try again.',
        /* ── Branch add / edit form (BranchAddEditModal) ── */
        'mb.common.optionalWord': 'Optional',
        'mb.branchForm.saving': 'Saving branch…',
        'mb.branchForm.addTitle': 'Add branch',
        'mb.branchForm.editTitle': 'Edit branch',
        'mb.branchForm.name': 'Branch name (English)',
        'mb.branchForm.nameAr': 'Branch name (Arabic)',
        'mb.branchForm.nameArPlaceholder': 'e.g. فرع وسط المدينة',
        'mb.branchForm.nameArHint': 'Optional. Customers using Devanza in Arabic see this name. If empty, they see the English name.',
        'mb.branchForm.namePlaceholder': 'e.g. Downtown',
        'mb.branchForm.location': 'Location',
        'mb.branchForm.locationHint': 'Search for an address, drag the pin, tap the map, or use your current location.',
        'mb.branchForm.searchPlaceholder': 'Search for an address…',
        'mb.branchForm.searchAria': 'Search address',
        'mb.branchForm.useLocation': 'Use my current location',
        'mb.branchForm.myLocation': 'My location',
        'mb.branchForm.dragPin': 'Drag the pin to adjust',
        'mb.branchForm.address': 'Address details',
        'mb.branchForm.country': 'Country',
        'mb.branchForm.discardAndGo': 'You have unsaved changes. Discard them and go to Business Details?',
        'mb.branchForm.editInDetails': 'Edit in Business Details →',
        'mb.branchForm.countryHint': 'All branches share your business’s country (same trade licence and tax registration).',
        'mb.branchForm.city': 'City',
        'mb.branchForm.state.emirate': 'Emirate (optional)',
        'mb.branchForm.state.governorate': 'Governorate (optional)',
        'mb.branchForm.state.region': 'Region (optional)',
        'mb.branchForm.state.generic': 'State / governorate (optional)',
        'mb.branchForm.selectPlaceholder': '— Select —',
        'mb.branchForm.street': 'Street address',
        'mb.branchForm.streetPlaceholder': 'e.g. 12 Al Wasl Road',
        'mb.branchForm.phone': 'Branch phone number',
        'mb.branchForm.phoneHint': 'For your team only — customers won’t see this number.',
        'mb.branchForm.settings': 'Branch settings',
        'mb.branchForm.settingsSub': 'Whether customers can see this branch, and which one is your main location.',
        'mb.branchForm.activeHelp': 'When on, customers can see this branch and book here. Turn it off to hide it for a while without deleting it.',
        'mb.branchForm.hq': 'Main branch (HQ)',
        'mb.branchForm.hqHelp': 'Your main location — used by default on receipts, invoices and your marketplace listing.',
        'mb.branchForm.description': 'Description',
        'mb.branchForm.descriptionHelp': 'Optional. Shown on your booking page and marketplace listing — keep it short and friendly.',
        'mb.branchForm.descriptionPlaceholder': 'e.g. Boutique salon in the heart of downtown — easy parking.',
        'mb.branchForm.geo.unsupported': 'Your browser can’t share your location.',
        'mb.branchForm.geo.denied': 'Location access was blocked — drag the pin instead.',
        'mb.branchForm.geo.failed': 'Couldn’t get your location. Try dragging the pin.',

        /* ── Validation messages (lib/ownerValidationMessages.ts) ── */
        'mb.validation.businessNameRequired': 'Business name is required.',
        'mb.validation.businessNameArRequired': 'Arabic business name is required.',
        'mb.validation.businessNameArTooLong': 'Arabic name is too long (max 200 characters).',
        'mb.validation.aboutRequired': 'Please describe your business in at least 50 characters.',
        'mb.validation.aboutTooLong': 'That’s too long (max 2,000 characters).',
        'mb.validation.aboutArRequired': 'Please write the Arabic description in at least 50 characters.',
        'mb.validation.aboutArTooLong': 'The Arabic description is too long (max 2,000 characters).',
        'mb.validation.countryRequired': 'Country is required.',
        'mb.validation.emailInvalid': 'Enter a valid email address.',
        'mb.validation.endBeforeStart': 'The end date must be on or after the start date.',
        'mb.validation.branchName': 'Branch name must be 3–100 letters or numbers.',
        'mb.validation.streetMin': 'Street address must be at least 5 characters.',
        'mb.validation.streetMax': 'Street address can’t be longer than 200 characters.',
        'mb.validation.cityRequired': 'City is required.',
        'mb.validation.cityMax': 'City can’t be longer than 100 characters.',
        'mb.validation.stateMax': 'This can’t be longer than 100 characters.',
        'mb.validation.countryMax': 'Country can’t be longer than 100 characters.',
        'mb.validation.invalidEntry': 'Please check this field — it contains characters we can’t accept.',
        'mb.validation.phoneInvalid': 'Enter a valid phone number.',
        'mb.validation.serviceNameMin': 'Service name must be at least 2 characters.',
        'mb.validation.serviceNameMax': 'Service name can’t be longer than 100 characters.',
        'mb.validation.branchRequired': 'Choose a branch.',
        'mb.validation.durationRequired': 'Choose a duration.',
        'mb.validation.descriptionMax': 'Description can’t be longer than 1,000 characters.',
        'mb.validation.tierRequired': 'Add at least one price option.',
        'mb.validation.tierNameRequired': 'Name this price option.',
        'mb.validation.tierNameMax': 'Option name can’t be longer than 100 characters.',
        'mb.validation.priceRequired': 'Enter a price.',
        'mb.validation.priceMin': 'Price can’t be negative.',
        /* ── Business Details ── */
        'mb.details.title': 'Business details',
        'mb.details.subtitle': 'Tell us about your business — logo, contact details, hours and description.',
        'mb.details.intro.strong': 'Set up your business at your own pace.',
        'mb.details.intro.body': 'Save anytime and come back to any section — nothing here stops you from exploring Devanza.',
        'mb.details.loading': 'Loading your business profile…',
        'mb.details.loadFailed': 'Couldn’t load your business details.',
        'mb.details.loadFailedRetry': 'Please try again.',
        'mb.details.loadFailedTemporary': 'This looks like a temporary problem — your saved details are safe and will reappear once the connection recovers.',
        'mb.details.noBusiness.strong': 'You don’t have a business yet.',
        'mb.details.noBusiness.body': 'Saving turns on once your business profile exists. Your edits are kept on this device in the meantime.',
        'mb.details.missing.title': 'Complete these fields to publish your business',
        'mb.details.missing.nameAr': 'Business name in Arabic',
        'mb.details.missing.aboutEn': 'About your business in English (at least 50 characters)',
        'mb.details.missing.aboutAr': 'About your business in Arabic (at least 50 characters)',
        'mb.details.brand.title': 'Brand images',
        'mb.details.brand.sub': 'Your logo appears on receipts, emails and your booking page; the salon card image appears in the marketplace.',
        'mb.details.brand.logo': 'Logo',
        'mb.details.brand.cover': 'Salon card image',
        'mb.details.brand.replaceHint': 'Use “Replace” to change it',
        'mb.details.brand.logoUploadHint': 'Use “Upload” to add a logo',
        'mb.details.brand.coverUploadHint': 'Use “Upload” to add a salon card image',
        'mb.details.brand.logoSpec': 'PNG / JPG · up to 2 MB · square',
        'mb.details.brand.coverSpec': 'PNG / JPG · up to 5 MB · 4:3',
        'mb.details.logo.saveFirst': 'Save your business name first, then upload a logo.',
        'mb.details.logo.tooLarge': 'The logo is too large — the maximum is 2 MB.',
        'mb.details.logo.badType': 'The logo must be a PNG, JPG or WEBP image.',
        'mb.details.logo.uploaded': 'Logo uploaded.',
        'mb.details.logo.failed': 'Couldn’t upload the logo. Please try again.',
        'mb.details.cover.saveFirst': 'Save your business name first, then upload a salon card image.',
        'mb.details.cover.tooLarge': 'The image is too large — the maximum is 5 MB.',
        'mb.details.cover.badType': 'The image must be a PNG, JPG or WEBP file.',
        'mb.details.cover.uploaded': 'Salon card image uploaded.',
        'mb.details.cover.failed': 'Couldn’t upload the image. Please try again.',
        'mb.details.staffFallback': 'Team member #{id}',
        'mb.details.info.title': 'Business info',
        'mb.details.info.sub': 'The name customers see when they book with you.',
        'mb.details.info.nameEn': 'Business name (English)',
        'mb.details.info.nameAr': 'Business name (Arabic)',
        'mb.details.info.type': 'Business type',
        'mb.details.info.typeHint': '(pick up to 4)',
        'mb.details.info.typePlaceholder': 'Select business types…',
        'mb.details.info.removeType': 'Remove {name}',
        'mb.details.info.typeAtCap': '{max} selected — remove one to choose another.',
        'mb.details.info.typeCount': '{n} of {max} selected',
        'mb.details.type.hairSalon': 'Hair salon',
        'mb.details.type.beautySalon': 'Beauty salon',
        'mb.details.type.spa': 'Spa',
        'mb.details.type.barbershop': 'Barbershop',
        'mb.details.type.nailBar': 'Nail bar',
        'mb.details.type.massage': 'Massage',
        'mb.details.type.skinCare': 'Skin care',
        'mb.details.type.browsLashes': 'Brows & lashes',
        'mb.details.type.mixed': 'Mixed services',
        'mb.details.hours.title': 'Business hours',
        'mb.details.hours.sub': 'Set the weekly hours for each branch. When you save, changes apply only to the branches you choose.',
        'mb.details.holidays.title': 'Holidays & one-off closures',
        'mb.details.holidays.sub': 'Dates you’ll be closed — public holidays, vacations or a day off. These override your weekly hours.',
        'mb.details.holidays.seedTitle': 'Add {n} public holidays for {country} {year}?',
        'mb.details.holidays.seedNote': 'Includes Eid, national days and other observances. You can edit or remove any of them afterwards.',
        'mb.details.holidays.loadAll': 'Add all',
        'mb.details.holidays.empty': 'No closures yet. Add public holidays, vacations or any other day you’ll be closed.',
        'mb.details.holidays.from': 'Closed from',
        'mb.details.holidays.to': 'Closed until',
        'mb.details.holidays.range': 'Several days',
        'mb.details.holidays.reason': 'Reason for closing',
        'mb.details.holidays.reasonPlaceholder': 'Reason (e.g. Eid Al Fitr, vacation, training day)',
        'mb.details.holidays.remove': 'Remove this closure',
        'mb.details.holidays.add': 'Add closure',
        'mb.holiday.newYear': 'New Year’s Day',
        'mb.holiday.eidFitr': 'Eid Al Fitr (tentative)',
        'mb.holiday.arafat': 'Arafat Day (tentative)',
        'mb.holiday.eidAdha': 'Eid Al Adha (tentative)',
        'mb.holiday.hijriNewYear': 'Hijri New Year (tentative)',
        'mb.holiday.prophetBirthday': 'Prophet’s Birthday (tentative)',
        'mb.holiday.commemoration': 'Commemoration Day',
        'mb.holiday.uaeNational': 'UAE National Day',
        'mb.holiday.labour': 'Labour Day',
        'mb.holiday.independence': 'Independence Day',
        'mb.holiday.christmas': 'Christmas Day',
        'mb.holiday.founding': 'Founding Day',
        'mb.holiday.national': 'National Day',
        'mb.holiday.copticChristmas': 'Coptic Christmas',
        'mb.holiday.revolution': 'Revolution Day',
        'mb.holiday.sinai': 'Sinai Liberation Day',
        'mb.holiday.june30': '30 June Revolution',
        'mb.holiday.armedForces': 'Armed Forces Day',
        'mb.holiday.nationalLiberation': 'National Day & Liberation Day',
        'mb.holiday.renaissance': 'Renaissance Day',
        'mb.holiday.sports': 'National Sports Day',
        'mb.details.about.title': 'About your business',
        'mb.details.about.sub': 'A short description for your public profile, in both languages — at least 50 characters each.',
        'mb.details.about.en': 'About (English)',
        'mb.details.about.ar': 'About (Arabic)',
        'mb.details.about.counter': '{n} / 50 characters',
        'mb.details.contact.title': 'Contact',
        'mb.details.contact.sub': 'How customers reach you outside of bookings.',
        'mb.details.contact.email': 'Email',
        'mb.details.contact.phone': 'Phone',
        'mb.details.contact.website': 'Website (optional)',
        'mb.details.social.title': 'Social media',
        'mb.details.social.sub': 'Add your social accounts so customers can find and follow you.',
        'mb.details.social.whatsapp': 'WhatsApp number, e.g. +971 50 123 4567',
        'mb.details.social.maps': 'Google Maps link or Plus Code',
        'mb.details.other.title': 'Other settings',
        'mb.details.other.sub': 'Who you serve, how prices are shown, and how bookings are handled.',
        'mb.details.other.salonType': 'Salon serves',
        'mb.details.other.salonType.both': 'Women and men',
        'mb.details.other.salonType.male': 'Men',
        'mb.details.other.salonType.female': 'Women',
        'mb.details.other.decimals': 'Price decimals',
        'mb.details.other.decimals.0': 'Whole numbers (150)',
        'mb.details.other.decimals.2': '2 decimals (150.00)',
        'mb.details.other.decimals.3': '3 decimals (150.000)',
        'mb.details.other.autoConfirm': 'Confirm bookings automatically',
        'mb.details.other.autoConfirmHelp': 'New bookings are confirmed straight away, without you approving each one.',
        'mb.details.other.sameDay': 'Let customers request same-day slots',
        'mb.details.other.sameDayHelp': 'Customers can ask for an appointment today, in the next free slot.',
        'mb.details.other.ratings': 'Show ratings publicly',
        'mb.details.other.ratingsHelp': 'Show your average star rating on your marketplace profile.',
        'mb.details.other.localOnly': '(this device only)',
        'mb.details.saveHint': 'Changes are saved to your business profile.',
        /* ── Categories ── */
        'mb.categories.title': 'Categories',
        'mb.categories.subtitle': 'Categories tell customers what your salon offers. They power marketplace search filters, the badges on your profile and how your services are grouped — set them up before adding services.',
        'mb.categories.loadFailed': 'Couldn’t load your categories.',
        'mb.categories.masterLoadFailed': 'Couldn’t load the category list. Please try again.',
        'mb.categories.addFailed': 'Couldn’t add that category. Please try again.',
        'mb.categories.removeConfirm': 'Customers won’t find your salon under “{name}” any more. Continue?',
        'mb.categories.removed': 'Removed “{name}”.',
        'mb.categories.removeFailed': 'Couldn’t remove that category. Please try again.',
        'mb.categories.hero.hide': 'Hide explanation',
        'mb.categories.hero.title': 'Categories help customers find you',
        'mb.categories.hero.body': 'Pick the categories that match what your salon does best. Each one is another place customers can find you — in search filters, on your profile and in Devanza’s marketing.',
        'mb.categories.hero.searchTitle': 'Marketplace search',
        'mb.categories.hero.searchBody': 'Customers filtering by “Hair styling” or “Henna” see your salon in the results.',
        'mb.categories.hero.badgesTitle': 'Profile badges',
        'mb.categories.hero.badgesBody': 'The categories you pick show as badges on your salon’s public profile.',
        'mb.categories.hero.marketingTitle': 'Marketing reach',
        'mb.categories.hero.marketingBody': 'Devanza runs campaigns for specific categories, and salons in those categories benefit.',
        'mb.categories.hero.previewLabel': 'How you’ll appear:',
        'mb.categories.hero.previewEmpty': 'Pick your first category to see a preview here.',
        'mb.categories.hero.reopen': 'Why categories matter — show again',
        'mb.categories.empty.title': 'Pick your first category',
        'mb.categories.empty.desc': 'Start with what your salon is best known for — you can add more anytime. The list includes regional favourites like Moroccan bath and henna alongside hair, nails and spa.',
        'mb.categories.browse': '+ Browse categories',
        'mb.categories.count.one': '{n} category',
        'mb.categories.count.other': '{n} categories',
        'mb.categories.cardMeta': 'Customers will find you under this category',
        'mb.categories.removeAria': 'Remove {name}',

        /* ── Browse categories (CategoryBrowseModal) ── */
        'mb.categoryBrowse.title': 'Browse categories',
        'mb.categoryBrowse.subtitle': 'Pick the categories that match your salon — customers will find you under each one you add.',
        'mb.categoryBrowse.search': 'Search categories…',
        'mb.categoryBrowse.noMatch': 'No categories match “{q}”.',
        'mb.categoryBrowse.noMatchHint': 'Try a different word.',
        'mb.categoryBrowse.loading': 'Loading categories…',
        'mb.categoryBrowse.already': 'Already in your list',
        'mb.categoryBrowse.hint': 'Customers browsing this category will see your salon',
        'mb.categoryBrowse.added': '✓ Added',
        'mb.categoryBrowse.adding': 'Adding…',
        'mb.categoryBrowse.add': '+ Add',
        'mb.categoryBrowse.footer': 'Adding a category is instant — customers see your salon under it straight away.',
        /* ── Services (My Business) ── */
        'mb.services.title': 'Services',
        'mb.services.fromPrice': 'from {price}',
        'mb.services.performedBy': 'Performed by',
        'mb.services.homeStaffHint': 'Home-visit bookings need a list of team members who can travel.',
        'mb.services.homeStaffPick': '⚠ Choose who does home visits',
        'mb.services.anyStaffHint': 'To narrow it down, choose specific team members in Manage Staff.',
        'mb.services.anyStaff': 'Any active team member',
        'mb.services.moreStaff': '+{n} more',
        'mb.services.loadFailed': 'Couldn’t load your services.',
        'mb.services.needCategory': 'Set up at least one category first.',
        'mb.services.needBranch': 'Add an active branch in Branches before creating a service.',
        'mb.services.toast.created': 'Service added.',
        'mb.services.toast.updated': 'Service updated.',
        'mb.services.toast.deleted': 'Service deleted.',
        'mb.services.toast.createFailed': 'Couldn’t add the service. Please try again.',
        'mb.services.toast.updateFailed': 'Couldn’t update the service. Please try again.',
        'mb.services.toast.deleteFailed': 'Couldn’t delete the service. Please try again.',
        'mb.services.uncategorized': 'Uncategorised',
        'mb.services.tabsAria': 'Services view',
        'mb.services.tab.manage': 'Manage services',
        'mb.services.tab.copy': 'Copy services to another branch',
        'mb.services.intro.strong': 'Three published services unlock your marketplace listing.',
        'mb.services.intro.body': 'Customers search Devanza by service — the more you offer, the more visible you are.',
        'mb.services.empty.title': 'You haven’t added any services yet',
        'mb.services.empty.desc': 'Tell customers what you offer. Each service has its own price, duration and an optional photo. Three or more published services put your salon on the Devanza marketplace.',
        'mb.services.empty.cta': '+ Add your first service',
        'mb.services.noCategories.title': 'Set up your service categories first',
        'mb.services.noCategories.desc': 'Categories (hair, nails, massage and so on) organise your services and help customers filter in the marketplace. Pick the ones that fit your salon on the Categories page, then come back to add services under each one.',
        'mb.services.noCategories.cta': '+ Set up categories',
        'mb.services.noBranches.title': 'Add a branch before your first service',
        'mb.services.noBranches.desc': 'Every service belongs to a branch — that’s how each booking reaches the right location. Add at least one active branch, then come back to add your services.',
        'mb.services.noBranches.cta': '+ Add a branch',
        'mb.services.branchFilterAria': 'Filter by branch',
        'mb.services.allBranches': 'All branches',
        'mb.services.countFiltered': '{n} of {total}',
        'mb.services.count.one': '{n} service',
        'mb.services.count.other': '{n} services',
        'mb.services.searchPlaceholder': 'Search services by name…',
        'mb.services.searchAria': 'Search services',
        'mb.services.filter.category': 'Category',
        'mb.services.filter.categoryAria': 'Filter by category',
        'mb.services.filter.status': 'Status',
        'mb.services.filter.statusAria': 'Filter by status',
        'mb.services.filter.activeOnly': 'Active only',
        'mb.services.filter.inactiveOnly': 'Inactive only',
        'mb.services.add': '+ Add service',
        'mb.services.chip.search': 'Search: “{q}”',
        'mb.services.chip.clearSearch': 'Clear search',
        'mb.services.chip.category': 'Category: {name}',
        'mb.services.chip.clearCategory': 'Clear category filter',
        'mb.services.chip.status': 'Status: {status}',
        'mb.services.chip.clearStatus': 'Clear status filter',
        'mb.services.clearAll': 'Clear all',
        'mb.services.progress.title.one': '{n} more service until your marketplace listing goes live',
        'mb.services.progress.title.other': '{n} more services until your marketplace listing goes live',
        'mb.services.progress.desc': 'Publish at least {n} active services and customers searching Devanza can find your salon.',
        'mb.services.filterEmpty': 'No services match these filters.',
        'mb.services.homePill': '🏠 Home service',
        'mb.services.priceOptions': '{n} price options',
        'mb.services.deleteAria': 'Delete {name}',
        /* ── Service add / edit form (ServiceAddEditModal) ── */
        'mb.serviceForm.durationConflict': 'The minimum duration can’t be longer than the maximum.',
        'mb.serviceForm.image.badType': 'Please choose an image file.',
        'mb.serviceForm.image.tooLarge': 'The image must be 5 MB or smaller.',
        'mb.serviceForm.nameTaken': 'A service with this name already exists at this branch in this category. Please choose another name.',
        'mb.serviceForm.renameCopy': 'Give this copy its own name — customers will see it.',
        'mb.serviceForm.renameCopyHint': 'This is a copy at the same branch — rename it before saving.',
        'mb.serviceForm.creating': 'Adding service…',
        'mb.serviceForm.saving': 'Saving service…',
        'mb.serviceForm.addTitle': 'Add service',
        'mb.serviceForm.editTitle': 'Edit service',
        'mb.serviceForm.subtitle': 'What you offer, how long it takes and what it costs. Customers see this on your marketplace listing and booking page.',
        'mb.serviceForm.copyFrom': 'Copy from another service',
        'mb.serviceForm.copyFromHint': '(saves typing)',
        'mb.serviceForm.copyFromPlaceholder': '— Start blank, or pick a service to copy —',
        'mb.serviceForm.unknownBranch': 'Unknown branch',
        'mb.serviceForm.copyFromHelp': 'Copies the name, durations, service type, description and prices from another service. Adjust anything for this branch — the original isn’t affected.',
        'mb.serviceForm.photo': 'Service photo (optional)',
        'mb.serviceForm.photoHint': 'PNG or JPG, up to 5 MB. Square photos look best.',
        'mb.serviceForm.removeImage': 'Remove image',
        'mb.serviceForm.name': 'Service name (English)',
        'mb.serviceForm.nameAr': 'Service name (Arabic)',
        'mb.serviceForm.nameArPlaceholder': 'e.g. قص وتصفيف الشعر',
        'mb.serviceForm.nameArHint': 'Optional. Customers using Devanza in Arabic see the Arabic name and description. If empty, they see the English ones.',
        'mb.serviceForm.descriptionAr': 'Description in Arabic (optional)',
        'mb.serviceForm.descriptionArPlaceholder': 'A few sentences in Arabic about this service',
        'mb.serviceForm.namePlaceholder': 'e.g. Hair colour & blow-dry',
        'mb.serviceForm.selectCategory': '— Select a category —',
        'mb.serviceForm.categoryRequired': 'Choose a category.',
        'mb.serviceForm.branch': 'Branch',
        'mb.serviceForm.selectBranch': '— Select a branch —',
        'mb.serviceForm.serviceType': 'Who it’s for',
        'mb.serviceForm.type.everyone': 'Everyone',
        'mb.serviceForm.type.women': 'Women only',
        'mb.serviceForm.type.men': 'Men only',
        'mb.serviceForm.minDuration': 'Minimum duration',
        'mb.serviceForm.maxDuration': 'Maximum duration',
        'mb.serviceForm.tiers': 'Price options ({n})',
        'mb.serviceForm.tier': 'Option {n}',
        'mb.serviceForm.tierName': 'Option name',
        'mb.serviceForm.tierNamePlaceholder': 'e.g. Short hair',
        'mb.serviceForm.duration': 'Duration',
        'mb.serviceForm.price': 'Price',
        'mb.serviceForm.addTier': '+ Add another price option',
        'mb.serviceForm.description': 'Description (optional)',
        'mb.serviceForm.descriptionPlaceholder': 'What’s included, what to expect, any preparation…',
        'mb.serviceForm.settings': 'Service settings',
        'mb.serviceForm.settingsSub': 'Whether customers can book it, and where it can be done.',
        'mb.serviceForm.activeHelp': 'When on, customers can see and book this service. Turn it off to hide it for a while without deleting it.',
        'mb.serviceForm.home': 'Home service available',
        'mb.serviceForm.homeHelp': 'Turn on if your team can do this service at the customer’s home or office.',
        'mb.serviceForm.delete': 'Delete service',
        /* ── Copy services to another branch (CloneServicesPanel) ── */
        'mb.copy.services.one': '1 service',
        'mb.copy.services.other': '{n} services',
        'mb.copy.theDestination': 'the other branch',
        'mb.copy.max50': 'You can copy up to 50 services at a time. Run it again for the rest.',
        'mb.copy.toast.copied': 'Copied {services} to {branch}',
        'mb.copy.toast.skipped': 'skipped {n} already there',
        'mb.copy.toast.nothing': 'Nothing to copy.',
        'mb.copy.toast.failed': 'Couldn’t copy the services. Please try again.',
        'mb.copy.needTwo': 'Copying needs at least two branches.',
        'mb.copy.needTwoHint': 'Add another branch in Branches, then come back here to copy services across.',
        'mb.copy.intro': 'Pick services at one branch and copy them to another. Each copy is independent — editing it later doesn’t change the original.',
        'mb.copy.from': 'Copy from',
        'mb.copy.to': 'Copy to',
        'mb.copy.pickSource': '— Choose a branch —',
        'mb.copy.pickTarget': '— Choose a branch —',
        'mb.copy.pickSourceFirst': 'Choose the branch to copy from to see its services.',
        'mb.copy.sourceEmpty': 'This branch has no active services to copy. Choose a different branch.',
        'mb.copy.pickTargetNow': 'Now choose the branch to copy to.',
        'mb.copy.pickTargetHint': 'Then choose which of the {services} to copy.',
        'mb.copy.dimmed': 'Services already at {branch} are greyed out — copying them again would create duplicates. Tick the rest.',
        'mb.copy.toCopy': '{n} to copy',
        'mb.copy.allThere': 'all already at that branch',
        'mb.copy.already': 'Already at {branch}',
        'mb.copy.noneSelected': 'Nothing selected',
        'mb.copy.selected': '{services} selected',
        'mb.copy.cap': 'up to 50 at a time',
        'mb.copy.copying': 'Copying…',
        'mb.copy.cta': 'Copy {services} to {branch}',
        'mb.copy.ctaIdle': 'Copy',
        /* ── Standalone /services page (Services, ServiceCard, AddEditService) ── */
        'mb.legacy.categoryRemoved': 'Category removed.',
        'mb.legacy.service': 'Service',
        'mb.legacy.search': 'Search by service name or category…',
        'mb.legacy.moved.title': 'Categories have a new home',
        'mb.legacy.moved.body': 'Categories are now managed in My Business → Categories, with an easier way to browse and a guide to where each category appears for customers.',
        'mb.legacy.moved.cta': 'Open Categories →',
        'mb.legacy.addedCategories': 'Your categories',
        'mb.legacy.categoryActions': 'Actions for {name}',
        'mb.legacy.serviceActions': 'Actions for {name}',
        'mb.legacy.priceOptions': 'Price options',
        'mb.legacy.wizard.nameRequired': 'Enter a service name.',
        'mb.legacy.wizard.step': 'Step {n} of 3',
        'mb.legacy.wizard.step1': 'Service details',
        'mb.legacy.wizard.step2': 'Prices & duration',
        'mb.legacy.wizard.step3': 'Assign team members',
        'mb.legacy.wizard.uploadPhoto': 'Upload a service photo',
        'mb.legacy.wizard.active': 'Service active',
        'mb.legacy.wizard.durationLabel': 'Duration (hh:mm:ss)',
        'mb.legacy.wizard.roleManager': 'Manager',
        'mb.legacy.wizard.roleStaff': 'Team member',
        'mb.legacy.wizard.noStaff': 'No team members found',
        'mb.legacy.wizard.back': 'Back',
        'mb.legacy.wizard.next': 'Next',
        'mb.legacy.wizard.done': 'Done',
        /* ── Branch filter (BranchFilter) ── */
        'mb.branchFilter.loading': 'Loading branches…',
        'mb.branchFilter.none': 'No active branches yet',
        'mb.branchFilter.more': '+{n} more branches',
        'mb.branchFilter.filter': 'Filter',
        'mb.branchFilter.title': 'Filter by branch',
        'mb.branchFilter.desc': 'Choose the branches to include in these results.',
        'mb.branchFilter.selectAll': 'Select all branches',
        'mb.branchFilter.apply': 'Apply',
        /* ── Overrides of inline keys (SO-12 — plain salon language for the weekly-hours editor) ── */
        'hoursMatrix.subtitle.divergent': '{salonName} · {n} branches · {k} differ from the usual hours',
        'hoursMatrix.defaultRow.label': 'Usual hours',
        'hoursMatrix.defaultRow.sub.divergent.one': '⚠ 1 day is different at some branches',
        'hoursMatrix.defaultRow.sub.divergent.other': '⚠ {k} days are different at some branches',
        'hoursMatrix.defaultRow.pill.single': 'ONE BRANCH',
        'hoursMatrix.defaultRow.pill.divergent': 'MOST BRANCHES',
        'hoursMatrix.cell.matchesDefault': '(same as usual)',
        'hoursMatrix.cell.differsFromDefault': 'Different from the usual hours',
        'hoursMatrix.cell.stale': 'Changed by someone else',
        'hoursMatrix.branch.revertLink': '↺ Use the usual hours',
        'hoursMatrix.branch.revertAllLink': '↺ Use the usual hours for every day',
        'hoursMatrix.branch.hq': 'HQ',
        'hoursMatrix.branch.cellRevertTip': '↺ Use usual',
        'hoursMatrix.branch.lastDivergenceNote': 'All branches now have the same hours.',
        'hoursMatrix.revertConfirm.text': 'Set {branch} on {day} back to the usual hours ({newValue})?',
        'hoursMatrix.revertConfirm.apply': 'Use usual hours',
        'hoursMatrix.bulkModal.headsUp': 'Heads up: the branches you select will lose any hours you set for them separately.',
        'hoursMatrix.bulkModal.confirmLabel': 'Confirm and apply',
        'hoursMatrix.branchCard.readonlyHeader': 'Opening hours',
        'hoursMatrix.branchCard.readonlyHeader.matchesDefault': '· usual hours',
        'hoursMatrix.branchCard.readonlyHeader.differs.one': '· 1 day differs from the usual hours',
        'hoursMatrix.branchCard.readonlyHeader.differs.other': '· {n} days differ from the usual hours',
        'hoursMatrix.branchCard.editLink': 'Edit in Business hours →',
        'hoursMatrix.branchCard.explainer': 'You set opening hours for all branches in Business details → Business hours.',
        'hoursMatrix.branchCard.divergesNote': '· different from usual ({defaultValue})',
        'hoursMatrix.toast.revertSuccess': '{branch} · {day} set back to the usual hours',
        'hoursMatrix.toast.error.staleHours': 'Someone else changed these hours. We’ve loaded the latest — please check and save again.',
        'hoursMatrix.toast.error.validation': 'Some times aren’t valid. Please fix them and try again.',
        'hoursMatrix.foot.singleHint': 'Tap any day to change it · changes apply to {salonName} only',
        'hoursMatrix.foot.uniformHint': 'Changing the usual hours will ask before updating all {n} branches',
        'hoursMatrix.foot.divergentHint': 'Tap any day to change it · a change on one branch’s row affects that branch only',
        'hoursMatrix.foot.lastDivergenceHint': 'Once every branch has the same hours, you’ll see a single row for all of them.',
        'hoursMatrix.foot.staleHint': 'Saving is paused because someone else changed these hours — review their changes first.',
        'hoursMatrix.divergence.chip.one': '1 differs from usual',
        'hoursMatrix.divergence.chip.other': '{k} differ from usual',
        'hoursMatrix.legend.divergent': 'Different from the usual hours',
        'hoursMatrix.legend.stale': 'Changed by someone else — your edit differs from the latest saved hours',
    },
    ar: {
        /* ── Common ─────────────────────────────────────────────── */
        'mb.common.tryAgain': 'حاول مرة أخرى',
        'mb.common.retry': 'إعادة المحاولة',
        'mb.common.loading': 'جارٍ التحميل…',
        'mb.common.saving': 'جارٍ الحفظ…',
        'mb.common.edit': 'تعديل',
        'mb.common.delete': 'حذف',
        'mb.common.remove': 'إزالة',
        'mb.common.cancel': 'إلغاء',
        'mb.common.save': 'حفظ',
        'mb.common.add': 'إضافة',
        'mb.common.close': 'إغلاق',
        'mb.common.dismiss': 'إخفاء',
        'mb.common.more': 'المزيد',
        'mb.common.all': 'الكل',
        'mb.common.active': 'نشط',
        'mb.common.inactive': 'غير نشط',
        'mb.common.activeInactive': '{active} نشط · {inactive} غير نشط',
        'mb.common.discardChanges': 'تجاهل التغييرات غير المحفوظة؟',
        'mb.common.optional': '(اختياري)',
        'mb.common.upload': 'رفع',
        'mb.common.replace': 'استبدال',
        'mb.common.uploading': 'جارٍ الرفع…',

        /* ── Durations ──────────────────────────────────────────── */
        'mb.duration.minutes.one': 'دقيقة',
        'mb.duration.minutes.two': 'دقيقتان',
        'mb.duration.minutes.few': '{n} دقائق',
        'mb.duration.minutes.other': '{n} دقيقة',
        'mb.duration.hours.one': 'ساعة',
        'mb.duration.hours.two': 'ساعتان',
        'mb.duration.hours.few': '{n} ساعات',
        'mb.duration.hours.other': '{n} ساعة',
        'mb.duration.join': '{h} و{m}',
        'mb.duration.range': '{min} – {max}',

        /* ── Delete confirm ─────────────────────────────────────── */
        'mb.deleteModal.title': 'حذف {name}؟',
        'mb.deleteModal.body': 'سيُحذف نهائيًا ولا يمكن التراجع عن ذلك.',
        'mb.deleteModal.confirm': 'حذف',

        /* ── Branches ───────────────────────────────────────────── */
        'mb.branches.title': 'الفروع',
        'mb.branches.subtitle': 'لكل فرع عنوانه وساعات عمله وفريقه وخدماته. يختار العملاء الفرع المناسب لهم عند الحجز.',
        'mb.branches.intro.strong': 'فرع واحد يكفي للبدء.',
        'mb.branches.intro.body': 'أضف المزيد في أي وقت — يصل كل حجز إلى الفرع الذي يختاره العميل.',
        'mb.branches.loadFailed': 'تعذّر تحميل فروعك.',
        'mb.branches.empty.title': 'لم تُضف أي فرع بعد',
        'mb.branches.empty.desc': 'أضف العنوان الذي تستقبل فيه الحجوزات. إذا كان لديك أكثر من صالون، أضف كل واحد منها — ليختار العملاء الأقرب عند الحجز.',
        'mb.branches.empty.cta': '+ أضف فرعك الأول',
        'mb.branches.empty.hint': 'العنوان ليس في متناولك الآن؟',
        'mb.branches.empty.hintDetails': 'تابع بتفاصيل المنشأة',
        'mb.branches.empty.hintOr': 'أو',
        'mb.branches.empty.hintServices': 'تصفّح الخدمات',
        'mb.branches.count': 'الفروع: {n} من {cap}',
        'mb.branches.add': '+ إضافة فرع',
        'mb.branches.hq': 'الفرع الرئيسي',
        'mb.branches.editAria': 'تعديل {name}',
        'mb.branches.team': 'فريق الفرع',
        'mb.branches.teamAria': 'فريق فرع {name}',
        'mb.branches.breakRulesAria': 'قواعد الاستراحة لفرع {name}',
        'mb.branches.moreAria': 'إجراءات أخرى لفرع {name}',
        'mb.branches.delete': 'حذف الفرع',
        'mb.branches.toast.created': 'تمت إضافة الفرع.',
        'mb.branches.toast.updated': 'تم تحديث الفرع.',
        'mb.branches.toast.deleted': 'تم حذف الفرع.',
        'mb.branches.toast.createFailed': 'تعذّرت إضافة الفرع. يُرجى المحاولة مرة أخرى.',
        'mb.branches.toast.updateFailed': 'تعذّر تحديث الفرع. يُرجى المحاولة مرة أخرى.',
        'mb.branches.toast.deleteFailed': 'تعذّر حذف الفرع. يُرجى المحاولة مرة أخرى.',
        'mb.branches.toast.teamLoadFailed': 'تعذّر تحميل فريقك. يُرجى المحاولة مرة أخرى.',
        'mb.branches.toast.teamUpdated': 'تم تحديث فريق {name}.',
        /* ── Branch team (BranchRosterModal) ── */
        'mb.common.saveChanges': 'حفظ التغييرات',
        'mb.team.title': 'فريق الفرع — {name}',
        'mb.team.subtitle': 'اختر من يعمل في هذا الفرع. من يحمل وسم «كل الفروع» يعمل في جميع الفروع، وإلغاء تحديده هنا يُبقيه في فروعك الأخرى فقط.',
        'mb.team.count': '{n} من {total} من أعضاء الفريق النشطين',
        'mb.team.added': '+{n} مُضاف',
        'mb.team.removed': '−{n} مُزال',
        'mb.team.empty': 'لا يوجد أعضاء نشطون في فريقك بعد. أضفهم من «إدارة الموظفين»، ثم عد لتختار من يعمل في هذا الفرع.',
        'mb.team.searchPlaceholder': 'ابحث بين {n} من أعضاء الفريق…',
        'mb.team.searchAria': 'البحث في أعضاء الفريق',
        'mb.team.allBranchesTag': 'كل الفروع',
        'mb.team.tipLabel': 'نصيحة:',
        'mb.team.tip': 'يمكنك أيضًا تحديد فروع كل موظف من «إدارة الموظفين». هذه الشاشة أسرع عند افتتاح فرع جديد.',
        'mb.team.noChanges': 'لا توجد تغييرات',
        'mb.team.addFailed': 'تعذّرت الإضافة إلى هذا الفرع. يُرجى المحاولة مرة أخرى.',
        'mb.team.removeFailed': 'تعذّرت الإزالة من هذا الفرع. يُرجى المحاولة مرة أخرى.',
        /* ── Branch add / edit form (BranchAddEditModal) ── */
        'mb.common.optionalWord': 'اختياري',
        'mb.branchForm.saving': 'جارٍ حفظ الفرع…',
        'mb.branchForm.addTitle': 'إضافة فرع',
        'mb.branchForm.editTitle': 'تعديل الفرع',
        'mb.branchForm.name': 'اسم الفرع (بالإنجليزية)',
        'mb.branchForm.nameAr': 'اسم الفرع (بالعربية)',
        'mb.branchForm.nameArPlaceholder': 'مثال: فرع وسط المدينة',
        'mb.branchForm.nameArHint': 'اختياري. يظهر هذا الاسم للعملاء الذين يستخدمون Devanza بالعربية. إن تُرك فارغًا، يظهر الاسم الإنجليزي.',
        'mb.branchForm.namePlaceholder': 'مثال: Downtown',
        'mb.branchForm.location': 'الموقع',
        'mb.branchForm.locationHint': 'ابحث عن عنوان، أو اسحب الدبوس، أو انقر على الخريطة، أو استخدم موقعك الحالي.',
        'mb.branchForm.searchPlaceholder': 'ابحث عن عنوان…',
        'mb.branchForm.searchAria': 'البحث عن العنوان',
        'mb.branchForm.useLocation': 'استخدام موقعي الحالي',
        'mb.branchForm.myLocation': 'موقعي',
        'mb.branchForm.dragPin': 'اسحب الدبوس لضبط الموقع',
        'mb.branchForm.address': 'تفاصيل العنوان',
        'mb.branchForm.country': 'الدولة',
        'mb.branchForm.discardAndGo': 'لديك تغييرات غير محفوظة. هل تريد تجاهلها والانتقال إلى تفاصيل المنشأة؟',
        'mb.branchForm.editInDetails': 'التعديل من تفاصيل المنشأة ←',
        'mb.branchForm.countryHint': 'تشترك جميع الفروع في دولة منشأتك (نفس الرخصة التجارية والتسجيل الضريبي).',
        'mb.branchForm.city': 'المدينة',
        'mb.branchForm.state.emirate': 'الإمارة (اختياري)',
        'mb.branchForm.state.governorate': 'المحافظة (اختياري)',
        'mb.branchForm.state.region': 'المنطقة (اختياري)',
        'mb.branchForm.state.generic': 'الولاية / المحافظة (اختياري)',
        'mb.branchForm.selectPlaceholder': '— اختر —',
        'mb.branchForm.street': 'عنوان الشارع',
        'mb.branchForm.streetPlaceholder': 'مثال: 12 Al Wasl Road',
        'mb.branchForm.phone': 'رقم هاتف الفرع',
        'mb.branchForm.phoneHint': 'لاستخدام فريقك فقط — لن يظهر هذا الرقم للعملاء.',
        'mb.branchForm.settings': 'إعدادات الفرع',
        'mb.branchForm.settingsSub': 'هل يمكن للعملاء رؤية هذا الفرع، وأي فرع هو موقعك الرئيسي.',
        'mb.branchForm.activeHelp': 'عند التفعيل، يرى العملاء هذا الفرع ويحجزون فيه. أوقفه لإخفائه مؤقتًا دون حذفه.',
        'mb.branchForm.hq': 'الفرع الرئيسي',
        'mb.branchForm.hqHelp': 'موقعك الرئيسي — يُستخدم افتراضيًا في الإيصالات والفواتير وصفحتك في السوق.',
        'mb.branchForm.description': 'الوصف',
        'mb.branchForm.descriptionHelp': 'اختياري. يظهر في صفحة الحجز وفي صفحتك في السوق — اجعله قصيرًا وودودًا.',
        'mb.branchForm.descriptionPlaceholder': 'مثال: صالون أنيق في قلب المدينة — مواقف سهلة.',
        'mb.branchForm.geo.unsupported': 'لا يدعم متصفحك مشاركة الموقع.',
        'mb.branchForm.geo.denied': 'تم حظر الوصول إلى الموقع — اسحب الدبوس بدلًا من ذلك.',
        'mb.branchForm.geo.failed': 'تعذّر تحديد موقعك. جرّب سحب الدبوس.',

        /* ── Validation messages (lib/ownerValidationMessages.ts) ── */
        'mb.validation.businessNameRequired': 'اسم المنشأة مطلوب.',
        'mb.validation.businessNameArRequired': 'اسم المنشأة بالعربية مطلوب.',
        'mb.validation.businessNameArTooLong': 'الاسم بالعربية طويل جدًا (200 حرف كحد أقصى).',
        'mb.validation.aboutRequired': 'يُرجى وصف منشأتك في 50 حرفًا على الأقل.',
        'mb.validation.aboutTooLong': 'النص طويل جدًا (2000 حرف كحد أقصى).',
        'mb.validation.aboutArRequired': 'يُرجى كتابة النبذة بالعربية في 50 حرفًا على الأقل.',
        'mb.validation.aboutArTooLong': 'النبذة بالعربية طويلة جدًا (2000 حرف كحد أقصى).',
        'mb.validation.countryRequired': 'الدولة مطلوبة.',
        'mb.validation.emailInvalid': 'أدخل بريدًا إلكترونيًا صحيحًا.',
        'mb.validation.endBeforeStart': 'يجب أن يكون تاريخ الانتهاء في يوم البدء أو بعده.',
        'mb.validation.branchName': 'يجب أن يتكوّن اسم الفرع من 3 إلى 100 حرف أو رقم.',
        'mb.validation.streetMin': 'يجب ألا يقل عنوان الشارع عن 5 أحرف.',
        'mb.validation.streetMax': 'لا يمكن أن يزيد عنوان الشارع عن 200 حرف.',
        'mb.validation.cityRequired': 'المدينة مطلوبة.',
        'mb.validation.cityMax': 'لا يمكن أن يزيد اسم المدينة عن 100 حرف.',
        'mb.validation.stateMax': 'لا يمكن أن يزيد هذا الحقل عن 100 حرف.',
        'mb.validation.countryMax': 'لا يمكن أن يزيد اسم الدولة عن 100 حرف.',
        'mb.validation.invalidEntry': 'يُرجى مراجعة هذا الحقل — يحتوي على أحرف غير مقبولة.',
        'mb.validation.phoneInvalid': 'أدخل رقم هاتف صحيحًا.',
        'mb.validation.serviceNameMin': 'يجب ألا يقل اسم الخدمة عن حرفين.',
        'mb.validation.serviceNameMax': 'لا يمكن أن يزيد اسم الخدمة عن 100 حرف.',
        'mb.validation.branchRequired': 'اختر فرعًا.',
        'mb.validation.durationRequired': 'اختر المدة.',
        'mb.validation.descriptionMax': 'لا يمكن أن يزيد الوصف عن 1000 حرف.',
        'mb.validation.tierRequired': 'أضف خيار سعر واحدًا على الأقل.',
        'mb.validation.tierNameRequired': 'أدخل اسمًا لخيار السعر.',
        'mb.validation.tierNameMax': 'لا يمكن أن يزيد اسم الخيار عن 100 حرف.',
        'mb.validation.priceRequired': 'أدخل السعر.',
        'mb.validation.priceMin': 'لا يمكن أن يكون السعر سالبًا.',
        /* ── Business Details ── */
        'mb.details.title': 'تفاصيل المنشأة',
        'mb.details.subtitle': 'عرّفنا بمنشأتك — الشعار وبيانات التواصل وساعات العمل والوصف.',
        'mb.details.intro.strong': 'جهّز منشأتك على راحتك.',
        'mb.details.intro.body': 'احفظ في أي وقت وعد إلى أي قسم لاحقًا — لا شيء هنا يمنعك من استكشاف Devanza.',
        'mb.details.loading': 'جارٍ تحميل ملف منشأتك…',
        'mb.details.loadFailed': 'تعذّر تحميل تفاصيل منشأتك.',
        'mb.details.loadFailedRetry': 'يُرجى المحاولة مرة أخرى.',
        'mb.details.loadFailedTemporary': 'يبدو أنها مشكلة مؤقتة — تفاصيلك المحفوظة آمنة وستظهر مجددًا عند عودة الاتصال.',
        'mb.details.noBusiness.strong': 'لا توجد لديك منشأة بعد.',
        'mb.details.noBusiness.body': 'يتاح الحفظ بعد إنشاء ملف منشأتك. تُحفظ تعديلاتك على هذا الجهاز حتى ذلك الحين.',
        'mb.details.missing.title': 'أكمل هذه الحقول لنشر منشأتك',
        'mb.details.missing.nameAr': 'اسم المنشأة بالعربية',
        'mb.details.missing.aboutEn': 'نبذة عن منشأتك بالإنجليزية (50 حرفًا على الأقل)',
        'mb.details.missing.aboutAr': 'نبذة عن منشأتك بالعربية (50 حرفًا على الأقل)',
        'mb.details.brand.title': 'صور العلامة التجارية',
        'mb.details.brand.sub': 'يظهر شعارك في الإيصالات والرسائل وصفحة الحجز، وتظهر صورة بطاقة الصالون في السوق.',
        'mb.details.brand.logo': 'الشعار',
        'mb.details.brand.cover': 'صورة بطاقة الصالون',
        'mb.details.brand.replaceHint': 'استخدم «استبدال» لتغييرها',
        'mb.details.brand.logoUploadHint': 'استخدم «رفع» لإضافة شعار',
        'mb.details.brand.coverUploadHint': 'استخدم «رفع» لإضافة صورة بطاقة الصالون',
        'mb.details.brand.logoSpec': 'PNG / JPG · حتى 2 MB · مربّعة',
        'mb.details.brand.coverSpec': 'PNG / JPG · حتى 5 MB · بنسبة 4:3',
        'mb.details.logo.saveFirst': 'احفظ اسم منشأتك أولًا، ثم ارفع الشعار.',
        'mb.details.logo.tooLarge': 'الشعار كبير جدًا — الحد الأقصى 2 MB.',
        'mb.details.logo.badType': 'يجب أن يكون الشعار صورة PNG أو JPG أو WEBP.',
        'mb.details.logo.uploaded': 'تم رفع الشعار.',
        'mb.details.logo.failed': 'تعذّر رفع الشعار. يُرجى المحاولة مرة أخرى.',
        'mb.details.cover.saveFirst': 'احفظ اسم منشأتك أولًا، ثم ارفع صورة بطاقة الصالون.',
        'mb.details.cover.tooLarge': 'الصورة كبيرة جدًا — الحد الأقصى 5 MB.',
        'mb.details.cover.badType': 'يجب أن تكون الصورة بصيغة PNG أو JPG أو WEBP.',
        'mb.details.cover.uploaded': 'تم رفع صورة بطاقة الصالون.',
        'mb.details.cover.failed': 'تعذّر رفع الصورة. يُرجى المحاولة مرة أخرى.',
        'mb.details.staffFallback': 'عضو الفريق رقم {id}',
        'mb.details.info.title': 'معلومات المنشأة',
        'mb.details.info.sub': 'الاسم الذي يراه العملاء عند الحجز لديك.',
        'mb.details.info.nameEn': 'اسم المنشأة (بالإنجليزية)',
        'mb.details.info.nameAr': 'اسم المنشأة (بالعربية)',
        'mb.details.info.type': 'نوع النشاط',
        'mb.details.info.typeHint': '(اختر حتى 4)',
        'mb.details.info.typePlaceholder': 'اختر أنواع النشاط…',
        'mb.details.info.removeType': 'إزالة {name}',
        'mb.details.info.typeAtCap': 'تم اختيار {max} — أزل نوعًا لاختيار غيره.',
        'mb.details.info.typeCount': 'تم اختيار {n} من {max}',
        'mb.details.type.hairSalon': 'صالون شعر',
        'mb.details.type.beautySalon': 'صالون تجميل',
        'mb.details.type.spa': 'سبا',
        'mb.details.type.barbershop': 'صالون حلاقة رجالي',
        'mb.details.type.nailBar': 'ركن الأظافر',
        'mb.details.type.massage': 'مساج',
        'mb.details.type.skinCare': 'العناية بالبشرة',
        'mb.details.type.browsLashes': 'الحواجب والرموش',
        'mb.details.type.mixed': 'خدمات متنوعة',
        'mb.details.hours.title': 'ساعات العمل',
        'mb.details.hours.sub': 'حدّد ساعات العمل الأسبوعية لكل فرع. عند الحفظ، تُطبَّق التغييرات على الفروع التي تختارها فقط.',
        'mb.details.holidays.title': 'العطلات وأيام الإغلاق',
        'mb.details.holidays.sub': 'الأيام التي ستكون فيها مغلقًا — العطلات الرسمية أو الإجازات أو يوم راحة. تتقدّم هذه الأيام على ساعات العمل الأسبوعية.',
        'mb.details.holidays.seedTitle': 'هل تريد إضافة {n} من العطلات الرسمية في {country} لعام {year}؟',
        'mb.details.holidays.seedNote': 'تشمل الأعياد والأيام الوطنية والمناسبات الأخرى. يمكنك تعديل أي منها أو حذفه لاحقًا.',
        'mb.details.holidays.loadAll': 'إضافة الكل',
        'mb.details.holidays.empty': 'لا توجد أيام إغلاق بعد. أضف العطلات الرسمية أو الإجازات أو أي يوم ستكون فيه مغلقًا.',
        'mb.details.holidays.from': 'مغلق من',
        'mb.details.holidays.to': 'مغلق حتى',
        'mb.details.holidays.range': 'عدة أيام',
        'mb.details.holidays.reason': 'سبب الإغلاق',
        'mb.details.holidays.reasonPlaceholder': 'السبب (مثل عيد الفطر، إجازة، يوم تدريب)',
        'mb.details.holidays.remove': 'إزالة يوم الإغلاق هذا',
        'mb.details.holidays.add': 'إضافة يوم إغلاق',
        'mb.holiday.newYear': 'رأس السنة الميلادية',
        'mb.holiday.eidFitr': 'عيد الفطر (موعد تقريبي)',
        'mb.holiday.arafat': 'يوم عرفة (موعد تقريبي)',
        'mb.holiday.eidAdha': 'عيد الأضحى (موعد تقريبي)',
        'mb.holiday.hijriNewYear': 'رأس السنة الهجرية (موعد تقريبي)',
        'mb.holiday.prophetBirthday': 'المولد النبوي (موعد تقريبي)',
        'mb.holiday.commemoration': 'يوم الشهيد',
        'mb.holiday.uaeNational': 'اليوم الوطني لدولة الإمارات',
        'mb.holiday.labour': 'عيد العمال',
        'mb.holiday.independence': 'عيد الاستقلال',
        'mb.holiday.christmas': 'عيد الميلاد المجيد',
        'mb.holiday.founding': 'يوم التأسيس',
        'mb.holiday.national': 'اليوم الوطني',
        'mb.holiday.copticChristmas': 'عيد الميلاد القبطي',
        'mb.holiday.revolution': 'عيد الثورة',
        'mb.holiday.sinai': 'عيد تحرير سيناء',
        'mb.holiday.june30': 'ذكرى ثورة 30 يونيو',
        'mb.holiday.armedForces': 'عيد القوات المسلحة',
        'mb.holiday.nationalLiberation': 'العيد الوطني ويوم التحرير',
        'mb.holiday.renaissance': 'يوم النهضة',
        'mb.holiday.sports': 'اليوم الرياضي للدولة',
        'mb.details.about.title': 'نبذة عن منشأتك',
        'mb.details.about.sub': 'وصف قصير لصفحتك العامة باللغتين — 50 حرفًا على الأقل لكل منهما.',
        'mb.details.about.en': 'النبذة (بالإنجليزية)',
        'mb.details.about.ar': 'النبذة (بالعربية)',
        'mb.details.about.counter': '{n} / 50 حرفًا',
        'mb.details.contact.title': 'التواصل',
        'mb.details.contact.sub': 'كيف يتواصل معك العملاء خارج الحجوزات.',
        'mb.details.contact.email': 'البريد الإلكتروني',
        'mb.details.contact.phone': 'الهاتف',
        'mb.details.contact.website': 'الموقع الإلكتروني (اختياري)',
        'mb.details.social.title': 'وسائل التواصل الاجتماعي',
        'mb.details.social.sub': 'أضف حساباتك على وسائل التواصل ليجدك العملاء ويتابعوك.',
        'mb.details.social.whatsapp': 'رقم WhatsApp، مثل +971 50 123 4567',
        'mb.details.social.maps': 'رابط خرائط Google أو رمز Plus Code',
        'mb.details.other.title': 'إعدادات أخرى',
        'mb.details.other.sub': 'من تخدم، وكيف تُعرض الأسعار، وكيف تُدار الحجوزات.',
        'mb.details.other.salonType': 'يخدم الصالون',
        'mb.details.other.salonType.both': 'النساء والرجال',
        'mb.details.other.salonType.male': 'الرجال',
        'mb.details.other.salonType.female': 'النساء',
        'mb.details.other.decimals': 'الخانات العشرية للأسعار',
        'mb.details.other.decimals.0': 'أرقام صحيحة (150)',
        'mb.details.other.decimals.2': 'خانتان عشريتان (150.00)',
        'mb.details.other.decimals.3': 'ثلاث خانات عشرية (150.000)',
        'mb.details.other.autoConfirm': 'تأكيد الحجوزات تلقائيًا',
        'mb.details.other.autoConfirmHelp': 'تُؤكَّد الحجوزات الجديدة فورًا دون الحاجة إلى موافقتك على كل منها.',
        'mb.details.other.sameDay': 'السماح للعملاء بطلب مواعيد في اليوم نفسه',
        'mb.details.other.sameDayHelp': 'يمكن للعملاء طلب موعد اليوم في أقرب وقت متاح.',
        'mb.details.other.ratings': 'إظهار التقييمات للعامة',
        'mb.details.other.ratingsHelp': 'عرض متوسط تقييمك بالنجوم في صفحتك في السوق.',
        'mb.details.other.localOnly': '(على هذا الجهاز فقط)',
        'mb.details.saveHint': 'تُحفظ التغييرات في ملف منشأتك.',
        /* ── Categories ── */
        'mb.categories.title': 'الفئات',
        'mb.categories.subtitle': 'تُعرّف الفئات العملاء بما يقدّمه صالونك، وتعتمد عليها فلاتر البحث في السوق وشارات صفحتك وطريقة تجميع خدماتك — أعدّها قبل إضافة الخدمات.',
        'mb.categories.loadFailed': 'تعذّر تحميل فئاتك.',
        'mb.categories.masterLoadFailed': 'تعذّر تحميل قائمة الفئات. يُرجى المحاولة مرة أخرى.',
        'mb.categories.addFailed': 'تعذّرت إضافة هذه الفئة. يُرجى المحاولة مرة أخرى.',
        'mb.categories.removeConfirm': 'لن يجد العملاء صالونك ضمن «{name}» بعد الآن. هل تريد المتابعة؟',
        'mb.categories.removed': 'تمت إزالة «{name}».',
        'mb.categories.removeFailed': 'تعذّرت إزالة هذه الفئة. يُرجى المحاولة مرة أخرى.',
        'mb.categories.hero.hide': 'إخفاء الشرح',
        'mb.categories.hero.title': 'الفئات تساعد العملاء في الوصول إليك',
        'mb.categories.hero.body': 'اختر الفئات التي تعبّر عن أفضل ما يقدّمه صالونك. كل فئة مكان إضافي يجدك فيه العملاء — في فلاتر البحث وعلى صفحتك وفي حملات Devanza التسويقية.',
        'mb.categories.hero.searchTitle': 'البحث في السوق',
        'mb.categories.hero.searchBody': 'العملاء الذين يبحثون عن «تصفيف الشعر» أو «الحناء» يرون صالونك في النتائج.',
        'mb.categories.hero.badgesTitle': 'شارات الصفحة',
        'mb.categories.hero.badgesBody': 'تظهر الفئات التي تختارها كشارات على صفحة صالونك العامة.',
        'mb.categories.hero.marketingTitle': 'وصول تسويقي أوسع',
        'mb.categories.hero.marketingBody': 'تطلق Devanza حملات لفئات محددة، وتستفيد منها الصالونات المدرجة فيها.',
        'mb.categories.hero.previewLabel': 'كيف ستظهر:',
        'mb.categories.hero.previewEmpty': 'اختر فئتك الأولى لترى المعاينة هنا.',
        'mb.categories.hero.reopen': 'لماذا الفئات مهمة — عرض الشرح مجددًا',
        'mb.categories.empty.title': 'اختر فئتك الأولى',
        'mb.categories.empty.desc': 'ابدأ بما يشتهر به صالونك — يمكنك إضافة المزيد في أي وقت. تضم القائمة خدمات محلية مميزة مثل الحمام المغربي والحناء إلى جانب الشعر والأظافر والسبا.',
        'mb.categories.browse': '+ تصفّح الفئات',
        'mb.categories.count.one': 'فئة واحدة',
        'mb.categories.count.other': 'عدد الفئات: {n}',
        'mb.categories.cardMeta': 'سيجدك العملاء ضمن هذه الفئة',
        'mb.categories.removeAria': 'إزالة {name}',

        /* ── Browse categories (CategoryBrowseModal) ── */
        'mb.categoryBrowse.title': 'تصفّح الفئات',
        'mb.categoryBrowse.subtitle': 'اختر الفئات التي تناسب صالونك — سيجدك العملاء ضمن كل فئة تضيفها.',
        'mb.categoryBrowse.search': 'ابحث في الفئات…',
        'mb.categoryBrowse.noMatch': 'لا توجد فئات تطابق «{q}».',
        'mb.categoryBrowse.noMatchHint': 'جرّب كلمة أخرى.',
        'mb.categoryBrowse.loading': 'جارٍ تحميل الفئات…',
        'mb.categoryBrowse.already': 'موجودة في قائمتك',
        'mb.categoryBrowse.hint': 'سيرى العملاء صالونك عند تصفّح هذه الفئة',
        'mb.categoryBrowse.added': '✓ مُضافة',
        'mb.categoryBrowse.adding': 'جارٍ الإضافة…',
        'mb.categoryBrowse.add': '+ إضافة',
        'mb.categoryBrowse.footer': 'تتم إضافة الفئة فورًا — ويرى العملاء صالونك ضمنها مباشرة.',
        /* ── Services (My Business) ── */
        'mb.services.title': 'الخدمات',
        'mb.services.fromPrice': 'ابتداءً من {price}',
        'mb.services.performedBy': 'يقدّمها',
        'mb.services.homeStaffHint': 'حجوزات الزيارات المنزلية تحتاج إلى تحديد أعضاء الفريق القادرين على التنقّل.',
        'mb.services.homeStaffPick': '⚠ حدّد من يقوم بالزيارات المنزلية',
        'mb.services.anyStaffHint': 'لتحديد أشخاص بعينهم، اخترهم من «إدارة الموظفين».',
        'mb.services.anyStaff': 'أي عضو نشط في الفريق',
        'mb.services.moreStaff': '+{n} آخرون',
        'mb.services.loadFailed': 'تعذّر تحميل خدماتك.',
        'mb.services.needCategory': 'أضف فئة واحدة على الأقل أولًا.',
        'mb.services.needBranch': 'أضف فرعًا نشطًا من قسم الفروع قبل إنشاء خدمة.',
        'mb.services.toast.created': 'تمت إضافة الخدمة.',
        'mb.services.toast.updated': 'تم تحديث الخدمة.',
        'mb.services.toast.deleted': 'تم حذف الخدمة.',
        'mb.services.toast.createFailed': 'تعذّرت إضافة الخدمة. يُرجى المحاولة مرة أخرى.',
        'mb.services.toast.updateFailed': 'تعذّر تحديث الخدمة. يُرجى المحاولة مرة أخرى.',
        'mb.services.toast.deleteFailed': 'تعذّر حذف الخدمة. يُرجى المحاولة مرة أخرى.',
        'mb.services.uncategorized': 'بدون فئة',
        'mb.services.tabsAria': 'عرض الخدمات',
        'mb.services.tab.manage': 'إدارة الخدمات',
        'mb.services.tab.copy': 'نسخ الخدمات إلى فرع آخر',
        'mb.services.intro.strong': 'ثلاث خدمات منشورة تُظهر صالونك في السوق.',
        'mb.services.intro.body': 'يبحث العملاء في Devanza حسب الخدمة — كلما زادت خدماتك زاد ظهورك.',
        'mb.services.empty.title': 'لم تُضف أي خدمة بعد',
        'mb.services.empty.desc': 'عرّف العملاء بما تقدّمه. لكل خدمة سعرها ومدتها وصورة اختيارية. ثلاث خدمات منشورة أو أكثر تُظهر صالونك في سوق Devanza.',
        'mb.services.empty.cta': '+ أضف خدمتك الأولى',
        'mb.services.noCategories.title': 'أعدّ فئات خدماتك أولًا',
        'mb.services.noCategories.desc': 'تنظّم الفئات (الشعر، الأظافر، المساج وغيرها) خدماتك وتساعد العملاء على التصفية في السوق. اختر ما يناسب صالونك من صفحة الفئات، ثم عد لإضافة الخدمات تحت كل فئة.',
        'mb.services.noCategories.cta': '+ إعداد الفئات',
        'mb.services.noBranches.title': 'أضف فرعًا قبل خدمتك الأولى',
        'mb.services.noBranches.desc': 'كل خدمة تتبع فرعًا — وبهذا يصل كل حجز إلى الموقع الصحيح. أضف فرعًا نشطًا واحدًا على الأقل، ثم عد لإضافة خدماتك.',
        'mb.services.noBranches.cta': '+ إضافة فرع',
        'mb.services.branchFilterAria': 'التصفية حسب الفرع',
        'mb.services.allBranches': 'كل الفروع',
        'mb.services.countFiltered': '{n} من {total}',
        'mb.services.count.one': 'خدمة واحدة',
        'mb.services.count.other': 'عدد الخدمات: {n}',
        'mb.services.searchPlaceholder': 'ابحث عن خدمة بالاسم…',
        'mb.services.searchAria': 'البحث في الخدمات',
        'mb.services.filter.category': 'الفئة',
        'mb.services.filter.categoryAria': 'التصفية حسب الفئة',
        'mb.services.filter.status': 'الحالة',
        'mb.services.filter.statusAria': 'التصفية حسب الحالة',
        'mb.services.filter.activeOnly': 'النشطة فقط',
        'mb.services.filter.inactiveOnly': 'غير النشطة فقط',
        'mb.services.add': '+ إضافة خدمة',
        'mb.services.chip.search': 'البحث: «{q}»',
        'mb.services.chip.clearSearch': 'مسح البحث',
        'mb.services.chip.category': 'الفئة: {name}',
        'mb.services.chip.clearCategory': 'إزالة تصفية الفئة',
        'mb.services.chip.status': 'الحالة: {status}',
        'mb.services.chip.clearStatus': 'إزالة تصفية الحالة',
        'mb.services.clearAll': 'مسح الكل',
        'mb.services.progress.title.one': 'خدمة واحدة أخرى ليظهر صالونك في السوق',
        'mb.services.progress.title.other': 'بقي {n} من الخدمات ليظهر صالونك في السوق',
        'mb.services.progress.desc': 'انشر {n} خدمات نشطة على الأقل ليتمكّن العملاء الباحثون في Devanza من العثور على صالونك.',
        'mb.services.filterEmpty': 'لا توجد خدمات تطابق هذه التصفية.',
        'mb.services.homePill': '🏠 خدمة منزلية',
        'mb.services.priceOptions': 'خيارات السعر: {n}',
        'mb.services.deleteAria': 'حذف {name}',
        /* ── Service add / edit form (ServiceAddEditModal) ── */
        'mb.serviceForm.durationConflict': 'لا يمكن أن تكون المدة الدنيا أطول من المدة القصوى.',
        'mb.serviceForm.image.badType': 'يُرجى اختيار ملف صورة.',
        'mb.serviceForm.image.tooLarge': 'يجب ألا يتجاوز حجم الصورة 5 MB.',
        'mb.serviceForm.nameTaken': 'توجد خدمة بهذا الاسم في هذا الفرع وضمن هذه الفئة. يُرجى اختيار اسم آخر.',
        'mb.serviceForm.renameCopy': 'أعطِ هذه النسخة اسمًا خاصًا بها — سيراه العملاء.',
        'mb.serviceForm.renameCopyHint': 'هذه نسخة في الفرع نفسه — غيّر اسمها قبل الحفظ.',
        'mb.serviceForm.creating': 'جارٍ إضافة الخدمة…',
        'mb.serviceForm.saving': 'جارٍ حفظ الخدمة…',
        'mb.serviceForm.addTitle': 'إضافة خدمة',
        'mb.serviceForm.editTitle': 'تعديل الخدمة',
        'mb.serviceForm.subtitle': 'ما تقدّمه، وكم يستغرق، وكم يكلّف. يرى العملاء ذلك في صفحتك في السوق وصفحة الحجز.',
        'mb.serviceForm.copyFrom': 'النسخ من خدمة أخرى',
        'mb.serviceForm.copyFromHint': '(لتوفير الوقت)',
        'mb.serviceForm.copyFromPlaceholder': '— ابدأ من الصفر أو اختر خدمة لنسخها —',
        'mb.serviceForm.unknownBranch': 'فرع غير معروف',
        'mb.serviceForm.copyFromHelp': 'ينسخ الاسم والمدد ونوع الخدمة والوصف والأسعار من خدمة أخرى. عدّل ما تشاء لهذا الفرع — لن تتأثر الخدمة الأصلية.',
        'mb.serviceForm.photo': 'صورة الخدمة (اختياري)',
        'mb.serviceForm.photoHint': 'PNG أو JPG حتى 5 MB. تبدو الصور المربّعة أفضل.',
        'mb.serviceForm.removeImage': 'إزالة الصورة',
        'mb.serviceForm.name': 'اسم الخدمة (بالإنجليزية)',
        'mb.serviceForm.nameAr': 'اسم الخدمة (بالعربية)',
        'mb.serviceForm.nameArPlaceholder': 'مثال: قص وتصفيف الشعر',
        'mb.serviceForm.nameArHint': 'اختياري. يرى العملاء الذين يستخدمون Devanza بالعربية الاسم والوصف العربيين. إن تُركا فارغين، يظهر النص الإنجليزي.',
        'mb.serviceForm.descriptionAr': 'الوصف بالعربية (اختياري)',
        'mb.serviceForm.descriptionArPlaceholder': 'بضع جمل بالعربية عن هذه الخدمة',
        'mb.serviceForm.namePlaceholder': 'مثال: صبغة وتجفيف الشعر',
        'mb.serviceForm.selectCategory': '— اختر فئة —',
        'mb.serviceForm.categoryRequired': 'اختر فئة.',
        'mb.serviceForm.branch': 'الفرع',
        'mb.serviceForm.selectBranch': '— اختر فرعًا —',
        'mb.serviceForm.serviceType': 'الفئة المستهدفة',
        'mb.serviceForm.type.everyone': 'الجميع',
        'mb.serviceForm.type.women': 'للنساء فقط',
        'mb.serviceForm.type.men': 'للرجال فقط',
        'mb.serviceForm.minDuration': 'المدة الدنيا',
        'mb.serviceForm.maxDuration': 'المدة القصوى',
        'mb.serviceForm.tiers': 'خيارات السعر ({n})',
        'mb.serviceForm.tier': 'الخيار {n}',
        'mb.serviceForm.tierName': 'اسم الخيار',
        'mb.serviceForm.tierNamePlaceholder': 'مثال: شعر قصير',
        'mb.serviceForm.duration': 'المدة',
        'mb.serviceForm.price': 'السعر',
        'mb.serviceForm.addTier': '+ إضافة خيار سعر آخر',
        'mb.serviceForm.description': 'الوصف (اختياري)',
        'mb.serviceForm.descriptionPlaceholder': 'ما الذي تشمله الخدمة، وما الذي يتوقعه العميل، وأي تحضيرات…',
        'mb.serviceForm.settings': 'إعدادات الخدمة',
        'mb.serviceForm.settingsSub': 'هل يمكن للعملاء حجزها، وأين يمكن تقديمها.',
        'mb.serviceForm.activeHelp': 'عند التفعيل، يرى العملاء هذه الخدمة ويحجزونها. أوقفها لإخفائها مؤقتًا دون حذفها.',
        'mb.serviceForm.home': 'تتوفر خدمة منزلية',
        'mb.serviceForm.homeHelp': 'فعّلها إذا كان فريقك يستطيع تقديم هذه الخدمة في منزل العميل أو مكتبه.',
        'mb.serviceForm.delete': 'حذف الخدمة',
        /* ── Copy services to another branch (CloneServicesPanel) ── */
        'mb.copy.services.one': 'خدمة واحدة',
        'mb.copy.services.other': '{n} من الخدمات',
        'mb.copy.theDestination': 'الفرع الآخر',
        'mb.copy.max50': 'يمكنك نسخ 50 خدمة كحد أقصى في كل مرة. كرّر العملية للباقي.',
        'mb.copy.toast.copied': 'تم نسخ {services} إلى {branch}',
        'mb.copy.toast.skipped': 'تم تخطّي {n} موجودة مسبقًا',
        'mb.copy.toast.nothing': 'لا يوجد ما يُنسخ.',
        'mb.copy.toast.failed': 'تعذّر نسخ الخدمات. يُرجى المحاولة مرة أخرى.',
        'mb.copy.needTwo': 'يتطلّب النسخ فرعين على الأقل.',
        'mb.copy.needTwoHint': 'أضف فرعًا آخر من قسم الفروع، ثم عد إلى هنا لنسخ الخدمات.',
        'mb.copy.intro': 'اختر خدمات من فرع وانسخها إلى فرع آخر. كل نسخة مستقلة — تعديلها لاحقًا لا يغيّر الأصل.',
        'mb.copy.from': 'النسخ من',
        'mb.copy.to': 'النسخ إلى',
        'mb.copy.pickSource': '— اختر فرعًا —',
        'mb.copy.pickTarget': '— اختر فرعًا —',
        'mb.copy.pickSourceFirst': 'اختر الفرع الذي ستنسخ منه لعرض خدماته.',
        'mb.copy.sourceEmpty': 'لا توجد خدمات نشطة في هذا الفرع للنسخ. اختر فرعًا آخر.',
        'mb.copy.pickTargetNow': 'الآن اختر الفرع الذي ستنسخ إليه.',
        'mb.copy.pickTargetHint': 'بعدها اختر أيًّا من {services} تريد نسخه.',
        'mb.copy.dimmed': 'الخدمات الموجودة مسبقًا في {branch} معطّلة — نسخها مجددًا سيكرّرها. حدّد البقية.',
        'mb.copy.toCopy': '{n} للنسخ',
        'mb.copy.allThere': 'كلها موجودة في ذلك الفرع',
        'mb.copy.already': 'موجودة في {branch}',
        'mb.copy.noneSelected': 'لم يتم تحديد شيء',
        'mb.copy.selected': 'تم تحديد {services}',
        'mb.copy.cap': '50 كحد أقصى في كل مرة',
        'mb.copy.copying': 'جارٍ النسخ…',
        'mb.copy.cta': 'نسخ {services} إلى {branch}',
        'mb.copy.ctaIdle': 'نسخ',
        /* ── Standalone /services page (Services, ServiceCard, AddEditService) ── */
        'mb.legacy.categoryRemoved': 'تمت إزالة الفئة.',
        'mb.legacy.service': 'الخدمة',
        'mb.legacy.search': 'ابحث باسم الخدمة أو الفئة…',
        'mb.legacy.moved.title': 'للفئات مكان جديد',
        'mb.legacy.moved.body': 'تُدار الفئات الآن من «منشأتي» ← «الفئات»، مع طريقة أسهل للتصفّح وشرح لأماكن ظهور كل فئة للعملاء.',
        'mb.legacy.moved.cta': 'فتح الفئات ←',
        'mb.legacy.addedCategories': 'فئاتك',
        'mb.legacy.categoryActions': 'إجراءات {name}',
        'mb.legacy.serviceActions': 'إجراءات {name}',
        'mb.legacy.priceOptions': 'خيارات السعر',
        'mb.legacy.wizard.nameRequired': 'أدخل اسم الخدمة.',
        'mb.legacy.wizard.step': 'الخطوة {n} من 3',
        'mb.legacy.wizard.step1': 'تفاصيل الخدمة',
        'mb.legacy.wizard.step2': 'الأسعار والمدة',
        'mb.legacy.wizard.step3': 'تعيين أعضاء الفريق',
        'mb.legacy.wizard.uploadPhoto': 'رفع صورة للخدمة',
        'mb.legacy.wizard.active': 'الخدمة نشطة',
        'mb.legacy.wizard.durationLabel': 'المدة (ساعة:دقيقة:ثانية)',
        'mb.legacy.wizard.roleManager': 'مدير',
        'mb.legacy.wizard.roleStaff': 'عضو في الفريق',
        'mb.legacy.wizard.noStaff': 'لا يوجد أعضاء في الفريق',
        'mb.legacy.wizard.back': 'رجوع',
        'mb.legacy.wizard.next': 'التالي',
        'mb.legacy.wizard.done': 'تم',
        /* ── Branch filter (BranchFilter) ── */
        'mb.branchFilter.loading': 'جارٍ تحميل الفروع…',
        'mb.branchFilter.none': 'لا توجد فروع نشطة بعد',
        'mb.branchFilter.more': '+{n} أخرى',
        'mb.branchFilter.filter': 'تصفية',
        'mb.branchFilter.title': 'التصفية حسب الفرع',
        'mb.branchFilter.desc': 'اختر الفروع التي تريد تضمينها في هذه النتائج.',
        'mb.branchFilter.selectAll': 'تحديد كل الفروع',
        'mb.branchFilter.apply': 'تطبيق',
        /* ── Overrides of inline keys (SO-12 — plain salon language for the weekly-hours editor) ── */
        'hoursMatrix.subtitle.divergent': '{salonName} · عدد الفروع: {n} · {k} منها تختلف عن الساعات المعتادة',
        'hoursMatrix.defaultRow.label': 'الساعات المعتادة',
        'hoursMatrix.defaultRow.sub.divergent.one': '⚠ يوم واحد مختلف في بعض الفروع',
        'hoursMatrix.defaultRow.sub.divergent.other': '⚠ عدد الأيام المختلفة في بعض الفروع: {k}',
        'hoursMatrix.defaultRow.pill.single': 'فرع واحد',
        'hoursMatrix.defaultRow.pill.divergent': 'معظم الفروع',
        'hoursMatrix.cell.matchesDefault': '(كالمعتاد)',
        'hoursMatrix.cell.differsFromDefault': 'مختلف عن الساعات المعتادة',
        'hoursMatrix.cell.stale': 'عدّله شخص آخر',
        'hoursMatrix.branch.revertLink': '↺ استخدام الساعات المعتادة',
        'hoursMatrix.branch.revertAllLink': '↺ استخدام الساعات المعتادة لكل الأيام',
        'hoursMatrix.branch.hq': 'الرئيسي',
        'hoursMatrix.branch.cellRevertTip': '↺ كالمعتاد',
        'hoursMatrix.branch.lastDivergenceNote': 'أصبحت ساعات جميع الفروع متطابقة.',
        'hoursMatrix.revertConfirm.text': 'إعادة {branch} يوم {day} إلى الساعات المعتادة ({newValue})؟',
        'hoursMatrix.revertConfirm.apply': 'استخدام الساعات المعتادة',
        'hoursMatrix.bulkModal.headsUp': 'تنبيه: ستفقد الفروع التي تختارها أي ساعات حدّدتها لها بشكل منفصل.',
        'hoursMatrix.bulkModal.confirmLabel': 'التأكيد والتطبيق',
        'hoursMatrix.branchCard.readonlyHeader': 'ساعات العمل',
        'hoursMatrix.branchCard.readonlyHeader.matchesDefault': '· الساعات المعتادة',
        'hoursMatrix.branchCard.readonlyHeader.differs.one': '· يوم واحد يختلف عن الساعات المعتادة',
        'hoursMatrix.branchCard.readonlyHeader.differs.other': '· أيام تختلف عن الساعات المعتادة: {n}',
        'hoursMatrix.branchCard.editLink': 'التعديل من ساعات العمل ←',
        'hoursMatrix.branchCard.explainer': 'تُحدَّد ساعات عمل جميع الفروع من «تفاصيل المنشأة» ← «ساعات العمل».',
        'hoursMatrix.branchCard.divergesNote': '· مختلف عن المعتاد ({defaultValue})',
        'hoursMatrix.toast.revertSuccess': 'تمت إعادة {branch} · {day} إلى الساعات المعتادة',
        'hoursMatrix.toast.error.staleHours': 'عدّل شخص آخر هذه الساعات. حمّلنا أحدث نسخة — يُرجى المراجعة والحفظ مجددًا.',
        'hoursMatrix.toast.error.validation': 'بعض الأوقات غير صحيحة. يُرجى تصحيحها والمحاولة مرة أخرى.',
        'hoursMatrix.foot.singleHint': 'انقر على أي يوم لتغييره · تُطبَّق التغييرات على {salonName} فقط',
        'hoursMatrix.foot.uniformHint': 'تغيير الساعات المعتادة سيطلب تأكيدك قبل تحديث جميع الفروع ({n})',
        'hoursMatrix.foot.divergentHint': 'انقر على أي يوم لتغييره · التغيير في صف فرع معيّن يخصّ ذلك الفرع فقط',
        'hoursMatrix.foot.lastDivergenceHint': 'عندما تتطابق ساعات جميع الفروع، سيظهر صف واحد لها جميعًا.',
        'hoursMatrix.foot.staleHint': 'الحفظ متوقف لأن شخصًا آخر عدّل هذه الساعات — راجع تغييراته أولًا.',
        'hoursMatrix.divergence.chip.one': 'واحد يختلف عن المعتاد',
        'hoursMatrix.divergence.chip.other': '{k} تختلف عن المعتاد',
        'hoursMatrix.legend.divergent': 'مختلف عن الساعات المعتادة',
        'hoursMatrix.legend.stale': 'عدّله شخص آخر — تعديلك يختلف عن آخر ساعات محفوظة',
    },
};
